import React, { useCallback } from 'react';

import Button from 'components/DesignSystem/Button';
import BodyBold from 'components/Typography/BodyBold';
import BodyRegular from 'components/Typography/BodyRegular';
import SecaoStatusPill from 'components/Relatorios/shared/Aprovacao/SecaoStatusPill';
import useRelatorioAprovacao from 'hooks/Relatorios/Aprovacao/useRelatorioAprovacao';
import { formatarDataHora, labelDaSecao } from 'helper/Relatorios/Aprovacao';

import './index.scss';

export interface SecaoAprovacaoCardProps {
  slug: string;
}

/**
 * Faixa colorida acima do conteúdo de uma seção (fora de `/aprovacao`),
 * visível só em `modoRevisao`. Aprovada → "Revogar aprovação"; pendente →
 * "Aprovar esta seção"; dia publicado → sem botão (Q4). Dia `sem_informe` → nada,
 * como a `RevisaoBar` (Q8): não há o que aprovar num dia sem estudo.
 */
export default function SecaoAprovacaoCard({ slug }: SecaoAprovacaoCardProps) {
  const {
    config, modoRevisao, status, statusPorSlug, publicado, mutando, erroMutacao, aprovar, revogar,
  } = useRelatorioAprovacao();
  const secao = statusPorSlug[slug];

  const handleAprovar = useCallback(() => {
    aprovar([slug]).catch(() => undefined);
  }, [aprovar, slug]);

  const handleRevogar = useCallback(() => {
    revogar([slug]).catch(() => undefined);
  }, [revogar, slug]);

  if (!modoRevisao || !secao || status?.status === 'sem_informe') return null;

  const aprovada = secao.status === 'aprovada';
  const label = labelDaSecao(slug, config.secoes);

  return (
    <div className={`relatorio-aprovacao-secao-aprovacao-card ${aprovada ? 'relatorio-aprovacao-secao-aprovacao-card--aprovada' : 'relatorio-aprovacao-secao-aprovacao-card--pendente'}`}>
      <div className="relatorio-aprovacao-secao-aprovacao-card__info">
        <SecaoStatusPill status={secao.status} />
        <div className="relatorio-aprovacao-secao-aprovacao-card__textos">
          <BodyBold>{label}</BodyBold>
          {aprovada && secao.aprovacao && (
            <BodyRegular>{`Aprovada por ${secao.aprovacao.por} em ${formatarDataHora(secao.aprovacao.em)}`}</BodyRegular>
          )}
          {!aprovada && secao.ultima_edicao && (
            <BodyRegular>{`Editado por ${secao.ultima_edicao.por} em ${formatarDataHora(secao.ultima_edicao.em)}`}</BodyRegular>
          )}
        </div>
      </div>
      {!publicado && (
        <Button
          type={aprovada ? 'secondary' : 'primary'}
          className={aprovada ? 'relatorio-aprovacao-secao-aprovacao-card__revogar' : 'relatorio-aprovacao-secao-aprovacao-card__aprovar'}
          small
          disabled={mutando}
          onClick={aprovada ? handleRevogar : handleAprovar}
          icon={aprovada ? undefined : 'checkmark'}
        >
          {aprovada ? 'Revogar aprovação' : 'Aprovar esta seção'}
        </Button>
      )}
      {erroMutacao !== undefined && (
        <BodyRegular>
          <span className="relatorio-aprovacao-secao-aprovacao-card__erro">Não foi possível concluir a ação. Tente novamente.</span>
        </BodyRegular>
      )}
    </div>
  );
}
